import { Controller } from "@hotwired/stimulus";

const ACCORDION_REGISTRY_KEY = "__uiAccordionRegistry__";

function getAccordionRegistry() {
  if (!window[ACCORDION_REGISTRY_KEY]) {
    window[ACCORDION_REGISTRY_KEY] = new Map();
  }

  return window[ACCORDION_REGISTRY_KEY];
}

function unregisterController(registry, group, controller) {
  const controllers = registry.get(group);
  if (!controllers) return;

  controllers.delete(controller);

  if (controllers.size === 0) {
    registry.delete(group);
  }
}

export default class extends Controller {
  static targets = ["toggler", "content", "container", "checkbox", "selectionCount", "selectAll"];
  static values = { allowMultiple: { type: Boolean, default: false }, group: String };

  connect() {
    this.prepareInitialState();
    this.registerInstance();
    this.updateSelectionCount()
  }

  disconnect() {
    this.unregisterInstance();
  }

  toggle(event) {
    event.preventDefault();

    const toggler = event.currentTarget;
    const content = this.findContent(toggler);

    if (!content) return;

    const isOpen = toggler.dataset.state === "open";

    if (isOpen) {
      this.close(toggler, content);
    } else {
      if (!this.allowMultipleValue) {
        this.togglerTargets.forEach((otherToggler) => {
          if (otherToggler === toggler) return;
          if (otherToggler.dataset.state === "open") {
            const otherContent = this.findContent(otherToggler);
            this.close(otherToggler, otherContent);
          }
        });

        this.closeGroupPeers();
      }

      this.open(toggler, content);
    }
  }

  open(toggler, content) {
    this.applyState(toggler, content, true);
    this.dispatch("opened", { detail: { toggler, content } });
  }

  close(toggler, content) {
    this.applyState(toggler, content, false);
    this.dispatch("closed", { detail: { toggler, content } });
  }

  applyState(toggler, content, expanded, animate = true) {
    if (!content) return;

    const togglers = this.togglerTargets.filter(
      (candidate) =>
        candidate.dataset.accordionContentId ===
        toggler.dataset.accordionContentId,
    );

    const targetOpacity = expanded ? "1" : "0";

    if (!animate) {
      content.style.height = expanded ? "auto" : "0px";
      content.style.opacity = targetOpacity;
    } else {
      if (expanded) {
        content.style.opacity = "1";
        content.style.height = "auto";
        const targetHeight = content.scrollHeight;

        content.style.height = "0px";
        content.offsetHeight; // force reflow

        requestAnimationFrame(() => {
          content.style.height = `${targetHeight}px`;
        });
      } else {
        content.style.opacity = "0";
        const startHeight = content.scrollHeight;
        content.style.height = `${startHeight}px`;
        content.offsetHeight; // force reflow

        requestAnimationFrame(() => {
          content.style.height = "0px";
        });
      }

      const handleTransitionEnd = (event) => {
        if (event.target !== content) return;

        content.style.height = expanded ? "auto" : "0px";
        content.style.opacity = targetOpacity;
        content.removeEventListener("transitionend", handleTransitionEnd);
      };

      content.addEventListener("transitionend", handleTransitionEnd);
    }

    togglers.forEach((element) => {
      element.dataset.state = expanded ? "open" : "closed";
      element.setAttribute("aria-expanded", expanded ? "true" : "false");
    });

    content.dataset.state = expanded ? "open" : "closed";

    if (expanded) {
      content.classList.add("st-accordion__content--visible");
      this.containerTarget.classList.remove("tw-border-secondary");
      this.containerTarget.classList.add("tw-border-primary");
    } else {
      content.classList.remove("st-accordion__content--visible");
      this.containerTarget.classList.remove("tw-border-primary");
      this.containerTarget.classList.add("tw-border-secondary");
    }
  }

  prepareInitialState() {
    this.contentTargets.forEach((content) => {
      content.classList.add("tw-transition-none");
    });

    this.togglerTargets.forEach((toggler) => {
      const content = this.findContent(toggler);
      const expanded =
        toggler.dataset.state === "open" ||
        toggler.getAttribute("aria-expanded") === "true";

      this.applyState(toggler, content, expanded, false);
    });

    requestAnimationFrame(() => {
      this.contentTargets.forEach((content) => {
        content.classList.remove("tw-transition-none");

        if (content.dataset.state === "open") {
          content.style.height = "auto";
          content.style.opacity = "1";
        } else {
          content.style.height = "0px";
          content.style.opacity = "0";
        }
      });
    });
  }

  findContent(toggler) {
    const id = toggler.dataset.accordionContentId;
    if (!id) return null;

    return this.contentTargets.find(
      (content) => content.dataset.accordionId === id,
    );
  }

  registerInstance() {
    if (!this.hasGroupValue) return;

    const registry = getAccordionRegistry();
    if (!registry.has(this.groupValue)) {
      registry.set(this.groupValue, new Set());
    }

    registry.get(this.groupValue).add(this);
  }

  unregisterInstance() {
    if (!this.hasGroupValue) return;

    const registry = getAccordionRegistry();
    unregisterController(registry, this.groupValue, this);
  }

  closeGroupPeers() {
    if (!this.hasGroupValue) return;

    const registry = getAccordionRegistry();
    const controllers = registry.get(this.groupValue);
    if (!controllers) return;

    controllers.forEach((controller) => {
      if (controller === this) return;
      controller.collapseAll();
    });
  }

  collapseAll() {
    const processedIds = new Set();

    this.togglerTargets.forEach((toggler) => {
      if (toggler.dataset.state !== "open") return;

      const contentId = toggler.dataset.accordionContentId;
      if (processedIds.has(contentId)) return;

      const content = this.findContent(toggler);
      this.close(toggler, content);
      processedIds.add(contentId);
    });
  }

  // Toggle all checkboxes
  toggleAll(event) {
    const checked = event.target.checked

    this.checkboxTargets.forEach(cb => {
      cb.checked = checked
    })

    this.updateSelectionCount()
  }

  // Called on individual checkbox change
  updateSelectionCount() {
    const selectedCount = this.checkboxTargets.filter(cb => cb.checked).length
    const totalCount = this.checkboxTargets.length

    if (this.hasSelectionCountTarget) {
      this.selectionCountTarget.textContent = `Selected (${selectedCount})`
    }

    // Keep "Select all" checkbox in sync
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = selectedCount === totalCount && totalCount > 0
      this.selectAllTarget.indeterminate =
        selectedCount > 0 && selectedCount < totalCount
    }
  }

  stopToggle(event) {
    event.stopPropagation()
  }
}
